import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { lessonPlanAPI, worksheetAPI, questionPaperAPI, documentAPI } from '@/services/api';
import { useAuth } from '@/contexts/AuthContext';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs';
import {
  Home, BookOpen, Search, Upload, Loader2, Eye, Copy, Download, X,
  Calculator, FlaskConical, Globe2, Palette, Music, Languages, Sparkles,
  FileText, ClipboardList, Layers,
} from 'lucide-react';
import { toast } from 'sonner';

const SUBJECT_STYLE = (subject = '', kind = 'lesson') => {
  const s = subject.toLowerCase();
  const base = { icon: BookOpen };
  if (s.includes('math')) return { icon: Calculator, gradient: 'from-orange-500 via-amber-500 to-yellow-500' };
  if (s.includes('science') || s.includes('physics') || s.includes('chem') || s.includes('bio')) return { icon: FlaskConical, gradient: 'from-emerald-500 via-teal-500 to-cyan-500' };
  if (s.includes('social') || s.includes('history') || s.includes('geo') || s.includes('civic')) return { icon: Globe2, gradient: 'from-sky-500 via-blue-500 to-indigo-500' };
  if (s.includes('art') || s.includes('craft')) return { icon: Palette, gradient: 'from-rose-500 via-pink-500 to-fuchsia-500' };
  if (s.includes('music')) return { icon: Music, gradient: 'from-violet-500 via-purple-500 to-fuchsia-500' };
  if (s.includes('hindi') || s.includes('english') || s.includes('language') || s.includes('sanskrit')) return { icon: Languages, gradient: 'from-indigo-500 via-purple-500 to-pink-500' };
  const KIND_GRADIENT = {
    lesson: 'from-red-500 via-rose-500 to-orange-500',
    worksheet: 'from-amber-500 via-orange-500 to-red-500',
    qp: 'from-orange-500 via-amber-500 to-lime-500',
  };
  return { ...base, gradient: KIND_GRADIENT[kind] || 'from-stone-500 to-stone-700' };
};

const downloadBlob = (blob, filename) => {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a'); a.href = url; a.download = filename;
  document.body.appendChild(a); a.click(); a.remove();
  setTimeout(() => URL.revokeObjectURL(url), 5000);
};

const Chip = ({ children, testid }) => (
  <span data-testid={testid} className="text-[10px] uppercase tracking-widest bg-white/25 backdrop-blur px-2 py-0.5 rounded-full text-white/95 font-semibold border border-white/30">{children}</span>
);

const DocCard = ({ kind, item, onView, onUse, onDownload, busyKey, withAnswerKey, onToggleAnsKey }) => {
  const idKey = item.lesson_plan_id || item.worksheet_id || item.question_paper_id;
  const { icon: Icon, gradient } = SUBJECT_STYLE(item.subject, kind);
  const label = kind === 'lesson' ? 'Lesson Plan' : kind === 'worksheet' ? 'Worksheet' : 'Question Paper';
  const busyPdf = busyKey === `${idKey}-pdf`;
  const busyDocx = busyKey === `${idKey}-docx`;
  const meta = kind === 'lesson'
    ? `${item.days?.length || 0} day${(item.days?.length || 0) !== 1 ? 's' : ''}`
    : kind === 'worksheet'
    ? `${(item.questions?.length || 0)} Q · ${item.max_marks || 0}M`
    : `${(item.sections?.length || 0)} sections · ${item.max_marks || 0}M`;
  const showAnsKey = kind === 'qp';

  return (
    <Card data-testid={`lib-card-${idKey}`} className="group relative overflow-hidden border border-orange-100 bg-white hover:shadow-2xl hover:-translate-y-1.5 transition-all duration-300">
      <div className={`h-28 bg-gradient-to-br ${gradient} relative overflow-hidden`}>
        <div className="absolute inset-0 bg-[radial-gradient(circle_at_30%_20%,rgba(255,255,255,0.35),transparent_40%)]" />
        <Icon className="absolute right-4 top-4 w-12 h-12 text-white/80" strokeWidth={1.5} />
        <div className="absolute top-3 left-4"><Chip testid={`chip-${idKey}`}>{label}</Chip></div>
        <div className="absolute bottom-3 left-4 text-white">
          <p className="text-[10px] uppercase tracking-wider opacity-95">{item.class_name || 'Class'}</p>
          <p className="text-sm font-bold drop-shadow-sm">{item.subject || 'Subject'}</p>
        </div>
      </div>
      <div className="p-5">
        <p className="font-bold text-[#1C1917] mb-1 line-clamp-2 min-h-[2.5rem]">{item.chapter || item.title || 'Untitled'}</p>
        <div className="flex items-center gap-2 text-xs text-stone-500 mb-3">
          <span>{meta}</span>
          {item.board && <><span>·</span><span>{item.board}</span></>}
          {item.language && item.language !== 'en' && <><span>·</span><span className="text-orange-700 font-semibold uppercase">{item.language}</span></>}
          <span>·</span><span className="text-orange-600 font-semibold">Anonymous</span>
        </div>
        {showAnsKey && (
          <label data-testid={`ans-key-toggle-${idKey}`} className="flex items-center gap-2 text-xs text-stone-700 bg-orange-50 border border-orange-200 rounded-md px-2 py-1 mb-3 cursor-pointer hover:bg-orange-100 transition">
            <input
              type="checkbox"
              className="accent-orange-500"
              checked={!!withAnswerKey}
              onChange={(e) => onToggleAnsKey(idKey, e.target.checked)}
              data-testid={`ans-key-checkbox-${idKey}`}
            />
            <span>Include <strong>Answer Key</strong> in downloads</span>
          </label>
        )}
        <div className="grid grid-cols-4 gap-2">
          <Button data-testid={`view-btn-${idKey}`} size="sm" variant="outline" onClick={() => onView(item)} className="border border-orange-200 text-xs col-span-1">
            <Eye className="w-3 h-3" />
          </Button>
          <Button data-testid={`use-btn-${idKey}`} size="sm" onClick={() => onUse(item)} className="bg-gradient-to-r from-orange-500 to-amber-500 hover:from-orange-600 hover:to-amber-600 text-white text-xs col-span-1">
            <Copy className="w-3 h-3" />
          </Button>
          <Button data-testid={`dl-pdf-${idKey}`} size="sm" variant="outline" onClick={() => onDownload(item, 'pdf')} disabled={busyPdf} className="border border-orange-200 text-xs col-span-1">
            {busyPdf ? <Loader2 className="w-3 h-3 animate-spin" /> : 'PDF'}
          </Button>
          <Button data-testid={`dl-docx-${idKey}`} size="sm" variant="outline" onClick={() => onDownload(item, 'docx')} disabled={busyDocx} className="border border-orange-200 text-xs col-span-1">
            {busyDocx ? <Loader2 className="w-3 h-3 animate-spin" /> : 'DOCX'}
          </Button>
        </div>
      </div>
    </Card>
  );
};

const Library = () => {
  const navigate = useNavigate();
  const { user } = useAuth();
  const [tab, setTab] = useState('all');
  const [lessons, setLessons] = useState([]);
  const [worksheets, setWorksheets] = useState([]);
  const [papers, setPapers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [uploading, setUploading] = useState(false);
  const [uploadingQp, setUploadingQp] = useState(false);
  const [busyKey, setBusyKey] = useState('');
  const [preview, setPreview] = useState(null); // { kind, id, title }
  const [ansKeyMap, setAnsKeyMap] = useState({}); // id -> boolean
  const fileInputRef = useRef(null);
  const qpFileRef = useRef(null);
  const isAdmin = user?.role === 'admin';

  const setAnsKeyFor = (id, val) => setAnsKeyMap((m) => ({ ...m, [id]: val }));

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [lp, ws, qp] = await Promise.all([
        lessonPlanAPI.library().then((r) => r.data).catch(() => ({ groups: [] })),
        worksheetAPI.library().then((r) => r.data).catch(() => ({ groups: [] })),
        questionPaperAPI.library().then((r) => r.data).catch(() => ({ groups: [] })),
      ]);
      const flat = (groups, key) => (groups || []).flatMap((g) => g[key] || []);
      setLessons(flat(lp.groups, 'plans'));
      setWorksheets(flat(ws.groups, 'worksheets'));
      setPapers(flat(qp.groups, 'question_papers'));
    } catch { toast.error('Failed to load library'); }
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const filtered = useMemo(() => {
    const q = search.toLowerCase().trim();
    const match = (item) => !q || (item.subject || '').toLowerCase().includes(q) ||
      (item.class_name || '').toLowerCase().includes(q) || (item.chapter || '').toLowerCase().includes(q) ||
      (item.title || '').toLowerCase().includes(q);
    return { lessons: lessons.filter(match), worksheets: worksheets.filter(match), papers: papers.filter(match) };
  }, [lessons, worksheets, papers, search]);

  const total = filtered.lessons.length + filtered.worksheets.length + filtered.papers.length;

  const onView = (item) => {
    const id = item.lesson_plan_id || item.worksheet_id || item.question_paper_id;
    const kind = item.lesson_plan_id ? 'lesson' : item.worksheet_id ? 'worksheet' : 'qp';
    setPreview({ kind, id, title: item.chapter || item.title || 'Preview' });
  };

  const onUse = async (item) => {
    try {
      if (item.lesson_plan_id) {
        const { data } = await lessonPlanAPI.duplicate(item.lesson_plan_id);
        toast.success('Added to your plans');
        navigate(`/builder/${data.lesson_plan_id}`);
      } else if (item.worksheet_id) {
        const { data } = await worksheetAPI.duplicate(item.worksheet_id);
        toast.success('Added to your worksheets');
        navigate(`/worksheet/${data.worksheet_id}`);
      } else if (item.question_paper_id) {
        const { data } = await questionPaperAPI.duplicate(item.question_paper_id);
        toast.success('Added to your question papers');
        navigate(`/question-paper/${data.question_paper_id}`);
      }
    } catch (err) { toast.error(err.response?.data?.detail || 'Failed to duplicate'); }
  };

  const onDownload = async (item, fmt) => {
    const id = item.lesson_plan_id || item.worksheet_id || item.question_paper_id;
    const withAns = !!ansKeyMap[id];
    setBusyKey(`${id}-${fmt}`);
    try {
      let res;
      let safe = (item.chapter || item.title || item.subject || 'document').replace(/[^a-z0-9-_ ]/gi, '').slice(0, 40).trim();
      if (item.lesson_plan_id) {
        const api = fmt === 'docx' ? documentAPI.generateDOCX : documentAPI.generatePDF;
        res = await api(item.lesson_plan_id, 'landscape', []);
      } else if (item.worksheet_id) {
        res = fmt === 'docx' ? await worksheetAPI.downloadDocx(item.worksheet_id, withAns) : await worksheetAPI.downloadPdf(item.worksheet_id, withAns);
      } else {
        res = fmt === 'docx' ? await questionPaperAPI.downloadDocx(item.question_paper_id, withAns) : await questionPaperAPI.downloadPdf(item.question_paper_id, withAns);
      }
      const suffix = withAns && (item.worksheet_id || item.question_paper_id) ? '-with-answers' : '';
      downloadBlob(new Blob([res.data]), `${safe}${suffix}.${fmt}`);
      toast.success(`${fmt.toUpperCase()} downloaded${withAns && (item.worksheet_id || item.question_paper_id) ? ' (with answer key)' : ''}`);
    } catch (err) { toast.error(err.response?.data?.detail || 'Download failed'); }
    finally { setBusyKey(''); }
  };

  const handleBulkUploadQp = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploadingQp(true);
    try {
      const { data } = await questionPaperAPI.bulkUpload(files);
      toast.success(`Papers: ${data.success} imported, ${data.failed} failed`);
      data.errors?.slice(0, 3).forEach((err) => toast.error(err));
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk upload failed'); }
    finally {
      setUploadingQp(false);
      if (qpFileRef.current) qpFileRef.current.value = '';
    }
  };

  const handleBulkUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (!files.length) return;
    setUploading(true);
    try {
      const { data } = await lessonPlanAPI.bulkImport(files);
      toast.success(`Uploaded: ${data.success} succeeded, ${data.failed} failed`);
      await load();
    } catch (err) { toast.error(err.response?.data?.detail || 'Bulk upload failed'); }
    finally {
      setUploading(false);
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const previewUrl = preview ? (
    preview.kind === 'lesson' ? documentAPI.inlinePdfUrl(preview.id, 'landscape')
    : preview.kind === 'worksheet' ? worksheetAPI.inlinePdfUrl(preview.id, !!ansKeyMap[preview.id])
    : questionPaperAPI.inlinePdfUrl(preview.id, !!ansKeyMap[preview.id])
  ) : '';

  const renderGrid = (items, kind) =>
    items.length === 0 ? (
      <div className="text-center py-16">
        <div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 mx-auto mb-4 flex items-center justify-center shadow-lg">
          <Sparkles className="w-10 h-10 text-white" />
        </div>
        <p className="text-stone-600">No {kind === 'lesson' ? 'lesson plans' : kind === 'worksheet' ? 'worksheets' : 'question papers'} yet.</p>
      </div>
    ) : (
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
        {items.map((item) => {
          const idKey = item.lesson_plan_id || item.worksheet_id || item.question_paper_id;
          return (
            <DocCard key={idKey}
              kind={kind} item={item} onView={onView} onUse={onUse} onDownload={onDownload}
              busyKey={busyKey}
              withAnswerKey={ansKeyMap[idKey]}
              onToggleAnsKey={setAnsKeyFor} />
          );
        })}
      </div>
    );

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-amber-50/40 to-yellow-50/30">
      <header className="sticky top-0 z-40 backdrop-blur-xl bg-white/80 border-b border-orange-200/40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex justify-between items-center">
          <Button data-testid="home-button" variant="ghost" onClick={() => navigate('/dashboard')} className="text-orange-700"><Home className="w-5 h-5 mr-2" /> Home</Button>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-lg bg-gradient-to-br from-orange-500 to-amber-500 flex items-center justify-center shadow-md">
              <Layers className="w-5 h-5 text-white" />
            </div>
            <h1 className="text-xl font-bold text-[#1C1917]">Community Library</h1>
          </div>
          <div className="w-24" />
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-10">
        <div className="mb-8 flex flex-wrap justify-between items-end gap-4">
          <div>
            <p className="text-xs uppercase tracking-widest text-orange-600 mb-2 font-bold">Vibrant Community Library</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl tracking-tight text-[#1C1917] font-bold">
              Browse · Reuse · Download
            </h2>
            <p className="text-base text-stone-700 mt-2">{total} documents · PDF & DOCX available on every card</p>
          </div>
          <div className="flex items-center gap-3 flex-wrap">
            {isAdmin && (
              <>
                <input ref={fileInputRef} data-testid="admin-bulk-upload-input" type="file" multiple accept=".docx,.pdf" onChange={handleBulkUpload} className="hidden" />
                <Button data-testid="admin-bulk-upload-btn" disabled={uploading} onClick={() => fileInputRef.current?.click()} className="bg-gradient-to-r from-orange-500 to-red-500 hover:from-orange-600 hover:to-red-600 text-white rounded-md shadow-md">
                  {uploading ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Uploading...</>) : (<><Upload className="w-4 h-4 mr-2" /> Bulk Upload Lessons</>)}
                </Button>
                <input ref={qpFileRef} data-testid="admin-bulk-upload-qp-input" type="file" multiple accept=".pdf,.docx" onChange={handleBulkUploadQp} className="hidden" />
                <Button data-testid="admin-bulk-upload-qp-btn" disabled={uploadingQp} onClick={() => qpFileRef.current?.click()} className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-white rounded-md shadow-md">
                  {uploadingQp ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Parsing...</>) : (<><FileText className="w-4 h-4 mr-2" /> Bulk Upload Papers</>)}
                </Button>
              </>
            )}
            <div className="relative w-full sm:w-80">
              <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-orange-400" />
              <Input data-testid="library-search-input" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search subject, class, chapter..." className="pl-9 bg-white/80 border-orange-200" />
            </div>
          </div>
        </div>

        <Tabs value={tab} onValueChange={setTab} className="w-full">
          <TabsList data-testid="library-tabs" className="grid w-full md:w-fit md:inline-grid grid-cols-4 mb-8 bg-white/80 border border-orange-200 p-1 h-auto">
            <TabsTrigger data-testid="tab-all" value="all" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white px-4 py-2">
              <Layers className="w-4 h-4 mr-2" /> All ({total})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-lesson-plans" value="lessons" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white px-4 py-2">
              <BookOpen className="w-4 h-4 mr-2" /> Lesson Plans ({filtered.lessons.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-worksheets" value="worksheets" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white px-4 py-2">
              <ClipboardList className="w-4 h-4 mr-2" /> Worksheets ({filtered.worksheets.length})
            </TabsTrigger>
            <TabsTrigger data-testid="tab-question-papers" value="papers" className="data-[state=active]:bg-gradient-to-r data-[state=active]:from-orange-500 data-[state=active]:to-amber-500 data-[state=active]:text-white px-4 py-2">
              <FileText className="w-4 h-4 mr-2" /> Question Papers ({filtered.papers.length})
            </TabsTrigger>
          </TabsList>

          {loading ? (
            <div className="text-center py-24"><div className="animate-spin rounded-full h-12 w-12 border-b-2 border-orange-500 mx-auto" /></div>
          ) : (
            <>
              <TabsContent value="all">
                <div className="space-y-12">
                  {filtered.lessons.length > 0 && (<section><h3 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2"><BookOpen className="w-5 h-5" /> Lesson Plans</h3>{renderGrid(filtered.lessons.slice(0, 8), 'lesson')}</section>)}
                  {filtered.worksheets.length > 0 && (<section><h3 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2"><ClipboardList className="w-5 h-5" /> Worksheets</h3>{renderGrid(filtered.worksheets.slice(0, 8), 'worksheet')}</section>)}
                  {filtered.papers.length > 0 && (<section><h3 className="text-xl font-bold text-orange-700 mb-4 flex items-center gap-2"><FileText className="w-5 h-5" /> Question Papers</h3>{renderGrid(filtered.papers.slice(0, 8), 'qp')}</section>)}
                  {total === 0 && (<Card className="p-12 text-center border border-orange-200 bg-white/60"><div className="w-20 h-20 rounded-full bg-gradient-to-br from-orange-400 to-amber-500 mx-auto mb-6 flex items-center justify-center shadow-lg"><Sparkles className="w-10 h-10 text-white" /></div><h3 className="text-xl font-semibold">No documents yet</h3><p className="text-stone-600 mt-1">Publish a lesson plan, worksheet or question paper to fill this library.</p></Card>)}
                </div>
              </TabsContent>
              <TabsContent value="lessons">{renderGrid(filtered.lessons, 'lesson')}</TabsContent>
              <TabsContent value="worksheets">{renderGrid(filtered.worksheets, 'worksheet')}</TabsContent>
              <TabsContent value="papers">{renderGrid(filtered.papers, 'qp')}</TabsContent>
            </>
          )}
        </Tabs>
      </main>

      <Dialog open={!!preview} onOpenChange={(o) => !o && setPreview(null)}>
        <DialogContent data-testid="library-preview-modal" className="max-w-6xl h-[90vh] p-0">
          <DialogHeader className="p-4 border-b border-stone-200 flex-row items-center justify-between">
            <DialogTitle className="text-base">{preview?.title}</DialogTitle>
            <Button variant="ghost" size="sm" onClick={() => setPreview(null)}><X className="w-4 h-4" /></Button>
          </DialogHeader>
          {previewUrl && <iframe data-testid="library-preview-iframe" title="Preview" src={previewUrl} className="w-full h-full border-0" />}
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Library;
